import React, { useState, useEffect, useCallback } from "react";
import { TbEye, TbEyeOff, TbLock, TbSparkles, TbPlugConnected } from "react-icons/tb";
import { api } from "../../../lib/api";
import { Button, Field, Alert } from "../../../components";

// The AI provider key, tenant-wide. Lifted out of IntegrationsScreen unchanged when
// that screen became a catalogue.

const AiProviderPanel = ({ isAdmin }) => {
  const [tenant, setTenant] = useState(null);
  const [aiKey, setAiKey] = useState("");
  const [aiKeyDirty, setAiKeyDirty] = useState(false);
  const [showAiKey, setShowAiKey] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState("");
  const [aiSaved, setAiSaved] = useState("");
  const [aiTest, setAiTest] = useState(null);

  // The masked key and provider live on the tenant record, not on the GHL config.
  const loadTenant = useCallback(async () => {
    try {
      const t = await api.get("s1/api/tenant");
      setTenant(t);
      // The API never returns the real key — only a masked preview. That preview is what
      // sits in the box until someone types over it.
      setAiKey(t?.masked_openai_key || "");
      setAiKeyDirty(false);
    } catch (e) {
      setAiError(e.message || "Could not load the AI settings.");
    }
  }, []);

  useEffect(() => { loadTenant(); }, [loadTenant]);

  // Saved through PUT s1/api/tenant/ai, not the general tenant PUT: that one rewrites
  // the whole tenant row without COALESCE, so using it here would null the logos.
  const saveAi = async () => {
    setAiBusy(true); setAiError(""); setAiSaved(""); setAiTest(null);
    try {
      await api.put("s1/api/tenant/ai", {
        ai_provider: "openai",
        // Untouched means the box still holds the masked key the API gave us. Sending it
        // would be echoing a mask back as if it were a key, so send nothing at all and
        // let the stored key stand.
        ...(aiKeyDirty ? { openai_api_key: aiKey } : {}),
      });
      setAiSaved("AI settings saved.");
      setAiKeyDirty(false);
      await loadTenant();
      setTimeout(() => setAiSaved(""), 3000);
    } catch (e) {
      setAiError(e.message || "Could not save the AI settings.");
    } finally { setAiBusy(false); }
  };

  const testAi = async () => {
    setAiTest({ state: "testing", message: "" });
    try {
      // Only send a key that was actually typed. With no key in the body the endpoint
      // tests the one already stored, which is what "test the saved key" means.
      const body = { provider: "openai" };
      if (aiKeyDirty && aiKey) body.api_key = aiKey;
      const d = await api.post("s3/api/tenant/ai-key/test", body);
      setAiTest({ state: "ok", message: d?.message || "Connection successful." });
    } catch (e) {
      setAiTest({ state: "error", message: e.message || "Connection test failed." });
    }
  };

  return (
    <div className="v2-settings-form v2-ghl">
      <section className="v2-integration">
        <div className="v2-integration-head">
          <span className="v2-ghl-icon v2-ghl-icon--amber"><TbSparkles size={18} /></span>
          <div>
            <div className="v2-setting-name">AI Provider &amp; API Keys (Tenant-wide)</div>
            <div className="v2-setting-desc">
              The key the AI report builder uses. It applies to everyone in this workspace.
            </div>
          </div>
          {tenant?.has_openai_key && (
            <span className="v2-chip" style={{ marginLeft: "auto" }}>
              <TbPlugConnected size={13} /> Key stored
            </span>
          )}
        </div>

        <Alert>{aiError}</Alert>
        {aiSaved && <Alert tone="success">{aiSaved}</Alert>}

        <div className={`v2-ghl-status${tenant?.has_openai_key ? " on" : ""}`}>
          <span className="v2-ghl-dot" />
          {tenant?.has_openai_key
            ? "A key is stored — AI report generation is available"
            : "No key stored — AI report generation will fall back to the platform key"}
        </div>

        <div className="v2-grid2">
          <Field label="Provider">
            {/* Neither a select nor a read-only input. Anthropic columns exist in the
                schema but nothing in this UI writes them yet, and a box you cannot type
                in reads as a field that is broken rather than a value that is fixed. */}
            <div className="v2-ai-provider"><TbSparkles size={14} /> OpenAI</div>
            <p className="v2-ghl-help">The only provider wired up today.</p>
          </Field>
          <Field label={<><TbLock size={13} /> OpenAI API Key</>}>
            <div className="v2-ghl-secret">
              <input className="v2-input" type={showAiKey ? "text" : "password"}
                     value={aiKey} disabled={!isAdmin}
                     aria-label="OpenAI API key"
                     placeholder="sk-..."
                     onChange={(e) => { setAiKey(e.target.value); setAiKeyDirty(true); setAiTest(null); }} />
              <button type="button" className="v2-ghl-reveal"
                      aria-label={showAiKey ? "Hide key" : "Show key"}
                      onClick={() => setShowAiKey((v) => !v)}>
                {showAiKey ? <TbEyeOff size={16} /> : <TbEye size={16} />}
              </button>
            </div>
            <p className="v2-ghl-help">
              {tenant?.has_openai_key
                ? "A key is stored and shown masked. Type a new one to replace it, or clear the box to remove it."
                : "From platform.openai.com → API keys. Stored encrypted."}
            </p>
          </Field>
        </div>

        {aiTest && (
          <Alert tone={aiTest.state === "ok" ? "success" : aiTest.state === "error" ? "error" : "warning"}>
            {aiTest.state === "testing" ? "Testing the connection…" : aiTest.message}
          </Alert>
        )}

        <div className="v2-ghl-actions">
          <Button variant="primary" onClick={saveAi}
                  disabled={aiBusy || !isAdmin || (!aiKeyDirty && !!tenant)}>
            {aiBusy ? "Saving…" : "Save AI settings"}
          </Button>
          {/* Testing the stored key needs no input, so this stays available even when
              nothing has been typed. */}
          <Button onClick={testAi}
                  disabled={aiTest?.state === "testing" || (!tenant?.has_openai_key && !aiKeyDirty)}>
            Test connection
          </Button>
        </div>
      </section>
    </div>
  );
};

export default AiProviderPanel;
